import React, { Dispatch, SetStateAction, useMemo } from 'react'
import {
    AboveProgressBar,
    AboveProgressBarText,
    ActionsButtonsWrapper,
    Breadcrumbs,
    SecondTextBreadcrumbs,
    ForwardButton,
    actionsBack,
    actionsNext
} from './styles'

import { notificationStatus } from 'constants/notificationStatus'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { UserDataType } from 'types/autentication'
import { RootReducerState } from 'types/global'
import { NotificationReducer } from 'types/notification'
import { isGraveObitoOrHasImageRisk } from 'utils/notifyEvents'
import ArchiveTreatment from './Children/Buttons/ArchiveTreatment'
import AssumeTreatment from './Children/Buttons/AssumeTreatment'
import BackTreatment from './Children/Buttons/BackTreatment'
import FinishTreatment from './Children/Buttons/FinishTreatment'
import ForwardToFinish from './Children/Buttons/ForwardToFinish'
import ForwardToTreatment from './Children/Buttons/ForwardToTreatment'
import NextTreatment from './Children/Buttons/NextTreatment'
import RemoveGroupNotifications from './Children/Buttons/RemoveGroupNotifications'
import ReopenNotification from './Children/Buttons/ReopenNotification'
import NotifiableReopenBlockedAlert from './Children/NotifiableReopenBlockedAlert'
import { ActionsButtonStep1 } from '../NotificationData/Children/ActionsButtonStep1'
import {
    NOTIFIABLE_REOPEN_BLOCKED_MESSAGE,
    shouldShowNotifiableReopenBlockedAlert
} from 'utils/damage'
import {
    isLocationReopenBlocked,
    LOCATION_REOPEN_BLOCKED_MESSAGE,
    shouldShowLocationReopenBlockedAlert
} from 'utils/location'

interface HeaderProps {
    showActionsButtonStep1: boolean
    currentStepSelected: number
    forwardToFinish({ type }: {
        type: 'forward' | 'finish';
    }): void
    handleArchiveNotification: () => void
    handleOpenForwardNotification: () => void
    handleFinishNotification({ type }: {
        type: 'forward' | 'finish';
    }): void
    handleAssumeNotification: () => void
    isDirty: boolean
    notificationStatusState: string
    currentUser: UserDataType
    setCurrentStepSelected: Dispatch<SetStateAction<number>>
    handleCancel: (event: any) => void
    handleSaveEdit: (event: any) => void
    // Props específicas para tratamento em grupo
    isGroupTreatment?: boolean
    hasSelectedGroupNotifications?: boolean
    handleRemoveGroupNotifications?: () => void
    selectedGroupNotificationsCount?: number
    handleFinishGroupNotification?: () => void
    isJustificationFilled?: boolean
    handleReopenNotification?: () => void
    loggedInUserIsResponsible?: boolean
    canReopen?: boolean
    isNotifiableCircumstance?: boolean
    // Quando true, força exibição do alerta de reabertura bloqueada (ex.: tratamento em grupo)
    hasNotifiableReopenBlocked?: boolean
    hasLocationReopenBlocked?: boolean
}

const NotificationTreatmentHeader: React.FC<HeaderProps> = ({
    currentStepSelected,
    setCurrentStepSelected,
    handleArchiveNotification,
    handleOpenForwardNotification,
    handleFinishNotification,
    handleAssumeNotification,
    isDirty,
    notificationStatusState,
    currentUser,
    forwardToFinish,
    showActionsButtonStep1,
    handleCancel,
    handleSaveEdit,
    isGroupTreatment = false,
    hasSelectedGroupNotifications = false,
    handleRemoveGroupNotifications,
    selectedGroupNotificationsCount = 0,
    handleFinishGroupNotification,
    isJustificationFilled = false,
    handleReopenNotification,
    loggedInUserIsResponsible,
    canReopen = true,
    isNotifiableCircumstance,
    hasNotifiableReopenBlocked = false,
    hasLocationReopenBlocked = false
}) => {
    const archiveIconStyle = 'w-[16px] h-[17px]'

    const isDirtyStyle = !!isDirty && 'border-mediumGray text-mediumGray'

    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )

    const showReopenBlockedAlert = useMemo(() => {
        const isFinishedOrArchived =
            notificationStatusState === 'Finalizada' ||
            notificationStatusState === 'Arquivada'

        if (isGroupTreatment) {
            return isFinishedOrArchived && (hasNotifiableReopenBlocked || hasLocationReopenBlocked)
        }

        const damageId = notificationSelector.notificationById?.damage_level?.value
        const locationIncident = notificationSelector.notificationById?.location_incident

        return (
            shouldShowNotifiableReopenBlockedAlert(notificationStatusState, damageId) ||
            shouldShowLocationReopenBlockedAlert(notificationStatusState, locationIncident)
        )
    }, [
        isGroupTreatment,
        hasNotifiableReopenBlocked,
        hasLocationReopenBlocked,
        notificationStatusState,
        notificationSelector.notificationById?.damage_level?.value,
        notificationSelector.notificationById?.location_incident
    ])

    const locationReopenBlocked = useMemo(
        () => isLocationReopenBlocked(notificationSelector.notificationById?.location_incident),
        [notificationSelector.notificationById?.location_incident]
    )

    const reopenBlockedMessage = useMemo(() => {
        if (isGroupTreatment) {
            if (hasNotifiableReopenBlocked) {
                return NOTIFIABLE_REOPEN_BLOCKED_MESSAGE
            }
            if (hasLocationReopenBlocked) {
                return LOCATION_REOPEN_BLOCKED_MESSAGE
            }
            return undefined
        }

        const damageId = notificationSelector.notificationById?.damage_level?.value
        const locationIncident = notificationSelector.notificationById?.location_incident

        if (shouldShowNotifiableReopenBlockedAlert(notificationStatusState, damageId)) {
            return NOTIFIABLE_REOPEN_BLOCKED_MESSAGE
        }
        if (shouldShowLocationReopenBlockedAlert(notificationStatusState, locationIncident)) {
            return LOCATION_REOPEN_BLOCKED_MESSAGE
        }
        return undefined
    }, [
        isGroupTreatment,
        hasNotifiableReopenBlocked,
        hasLocationReopenBlocked,
        notificationStatusState,
        notificationSelector.notificationById?.damage_level?.value,
        notificationSelector.notificationById?.location_incident
    ])
    /**
     * Adcionados para controle de currentStepSelected atraves do indice no redux
     */
    const currentStepTreatments = notificationSelector?.notificationProcessRequiredStepsRules?.currentStepTreatments

    const damageLevel: { display_value: string, value: string } = useMemo(() => {
        return notificationSelector.notificationById?.damage_level
    }, [notificationSelector.notificationById])

    const imageRisk: { display_value: string, value: string } = useMemo(() => {
        return notificationSelector.notificationById?.u_image_risk
    }, [notificationSelector.notificationById])

    const getTitle = () => {
        if (isGroupTreatment) {
            // Verificar se o tratamento está finalizado
            if (notificationStatusState === 'Finalizada') {
                return 'Tratamento em Grupo Finalizado'
            }
            return 'Tratamento em Grupo'
        }

        const title = {
            [notificationStatus[0].label]: 'Análise da Notificação',
            [notificationStatus[1].label]: 'Notificação aguardando tratamento',
            [notificationStatus[2].label]: 'Notificação em tratamento',
            [notificationStatus[3].label]: 'Pendente Finalização',
            [notificationStatus[4].label]: 'Notificação finalizada',
            [notificationStatus[5].label]: 'Notificação arquivada'
        }
        return title[notificationStatusState]
    }

    const goBack = (params?: { type: 'forward' | 'finish' }) => {
        if (isGroupTreatment) {
            if (currentStepSelected > 1) {
                setCurrentStepSelected(currentStepSelected - 1)
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
            return
        }

        if(currentStepTreatments){
            let index  = currentStepTreatments.indexOf(currentStepSelected)
            if(index != -1 && index > 0){
                setCurrentStepSelected(currentStepTreatments[--index])
            }else if(index === 0){
                setCurrentStepSelected(1)
            }
        }
    }
    const goFoward = (params?: { type: 'forward' | 'finish' }) => {
        if (isGroupTreatment) {
            if (currentStepSelected < 6) {
                setCurrentStepSelected(currentStepSelected + 1)
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
            return
        }

        if(currentStepTreatments){
            if((currentStepTreatments.length + 2) === currentStepSelected){
                setCurrentStepSelected(currentStepSelected+2)
            }else if(currentStepSelected === 1){
                setCurrentStepSelected(currentStepTreatments[currentStepSelected-1])
            }else{
                let index  = currentStepTreatments.indexOf(currentStepSelected)
                setCurrentStepSelected(currentStepTreatments[++index])
            }
        }
    }

    const lessThanOne = () => {
        if (notificationStatusState &&
            (notificationStatusState === notificationStatus[0].label ||
                notificationStatusState === notificationStatus[1].label ||
                notificationStatusState === notificationStatus[5].label)) {
            return false;
        }
        return currentStepSelected > 1
    }
    const greaterThanFive = () => {
        if (notificationStatusState &&
            (notificationStatusState === notificationStatus[0].label ||
                notificationStatusState === notificationStatus[1].label)) {
            return false;
        }
        return currentStepSelected < 6
    }

    return (
        <>
            <Breadcrumbs>
                <Link className="mr-1 cursor-pointer" to="/notificacoes">
                    Gestão das notificações
                </Link>
                {' > '}
                <SecondTextBreadcrumbs>
                    {isGroupTreatment ? 'Tratamento em grupo' : 'Notificação'}
                </SecondTextBreadcrumbs>
            </Breadcrumbs>

            <AboveProgressBar>
                <AboveProgressBarText>{getTitle()}</AboveProgressBarText>


                {showActionsButtonStep1 ? (
                    <ActionsButtonStep1 handleCancel={handleCancel} handleSaveEdit={handleSaveEdit} />
                ) : (
                    <ActionsButtonsWrapper>
                        {isGroupTreatment && hasSelectedGroupNotifications ? (
                            <RemoveGroupNotifications
                                isDirty={isDirty}
                                isDirtyStyle={isDirtyStyle}
                                currentUser={currentUser}
                                handleRemoveNotifications={handleRemoveGroupNotifications || (() => {})}
                                hasSelectedNotifications={hasSelectedGroupNotifications}
                                selectedCount={selectedGroupNotificationsCount}
                            />
                        ) : (
                            <>
                                {/* Botão de reabertura - aparece na esquerda em qualquer aba quando finalizada */}
                                <ReopenNotification
                                    currentUser={currentUser}
                                    notificationStatusState={notificationStatusState}
                                    handleReopenNotification={handleReopenNotification || (() => {})}
                                    actionsBack={actionsBack}
                                    isDirty={isDirty}
                                    isDirtyStyle={isDirtyStyle}
                                    damageLevelValue={notificationSelector?.notificationById?.damage_level?.value}
                                    isNotifiableCircumstance={isNotifiableCircumstance}
                                    canReopen={canReopen && !locationReopenBlocked}
                                />
                                
                                {!isGroupTreatment && (
                                    <>
                                        <ArchiveTreatment
                                            loggedInUserIsResponsible={loggedInUserIsResponsible}
                                            archiveIconStyle={archiveIconStyle}
                                            isDirty={isDirty}
                                            isDirtyStyle={isDirtyStyle}
                                            currentUser={currentUser}
                                            handleArchiveNotification={handleArchiveNotification}
                                            notificationStatusState={notificationStatusState}
                                            currentStepSelected={currentStepSelected}
                                        />
                                        <ForwardToTreatment
                                            actionsBack={actionsBack}
                                            archiveIconStyle={archiveIconStyle}
                                            isDirty={isDirty}
                                            isDirtyStyle={isDirtyStyle}
                                            currentUser={currentUser}
                                            handleOpenForwardNotification={handleOpenForwardNotification}
                                            notificationStatusState={notificationStatusState}
                                            currentStepSelected={currentStepSelected}
                                        />
                                        <FinishTreatment
                                            isGraveObitoOrImage={
                                                isGraveObitoOrHasImageRisk({
                                                    damageLevelValue: notificationSelector?.notificationById?.damage_level?.value,
                                                    imageRiskValue: notificationSelector?.notificationById?.u_image_risk?.value
                                                })}
                                            isDirty={isDirty}
                                            isDirtyStyle={isDirtyStyle}
                                            currentUser={currentUser}
                                            handleFinishNotification={handleFinishNotification}
                                            notificationStatusState={notificationStatusState}
                                            loggedInUserIsResponsible={loggedInUserIsResponsible}
                                            currentStepSelected={currentStepSelected}
                                        />
                                        <AssumeTreatment
                                            notificationSector={notificationSelector?.notificationById?.incident_sector
                                                ?.value}
                                            notificationLocation={notificationSelector?.notificationById?.location_incident?.value}
                                            currentUser={currentUser}
                                            handleAssumeNotification={handleAssumeNotification}
                                            notificationStatusState={notificationStatusState}
                                            actionsBack={actionsBack}
                                            loggedInUserIsResponsible={loggedInUserIsResponsible}
                                            currentStepSelected={currentStepSelected}
                                        />
                                        <ForwardToFinish
                                            imageRisk={imageRisk}
                                            damageLevel={damageLevel}
                                            currentUser={currentUser}
                                            notificationStatusState={notificationStatusState}
                                            actionsBack={actionsBack}
                                            loggedInUserIsResponsible={loggedInUserIsResponsible}
                                            currentStepSelected={currentStepSelected}
                                            forwardToFinish={forwardToFinish}
                                        />
                                    </>
                                )}
                                <>
                                    <BackTreatment
                                        rendered={lessThanOne()}
                                        actionsBack={actionsBack}
                                        forwardToFinish={goBack}
                                    />
                                    {isGroupTreatment && currentStepSelected === 6 && notificationStatusState !== 'Finalizada' ? (
                                        <ForwardButton
                                            data-testid="test-finish-group-button"
                                            className={`${!loggedInUserIsResponsible && 'bg-disabled border-mediumGray text-mediumGray'}`}
                                            disabled={!loggedInUserIsResponsible}
                                            type="button"
                                            onClick={() => {
                                                handleFinishGroupNotification()
                                            }}
                                        >
                                            Finalizar tratamento em grupo
                                        </ForwardButton>
                                    ) : isGroupTreatment && currentStepSelected === 6 && notificationStatusState === 'Finalizada' ? (
                                        // Não mostrar o botão de finalizar na última etapa quando já finalizada
                                        null
                                    ) : (
                                        <NextTreatment
                                            rendered={greaterThanFive()}
                                            actionsBack={actionsNext}
                                            forwardToFinish={goFoward}
                                        />
                                    )}
                                </>
                            </>
                        )}
                    </ActionsButtonsWrapper>
                )}
            </AboveProgressBar>
            <NotifiableReopenBlockedAlert
                show={showReopenBlockedAlert}
                message={reopenBlockedMessage}
            />
        </>
    )
}

export default NotificationTreatmentHeader
